import { useCallback, useEffect, useMemo, useState } from 'react';
import CmsAPI from '../../common/apis/CmsAPI';
import format from 'date-fns/format';
import { useTranslation } from 'react-i18next';

import en from '../utils/translations/en.json';
import de from '../utils/translations/de.json';
import nl from '../utils/translations/nl.json';
import { parseString } from '../utils/stringHelper';
import { ToastNotification } from '../components/Toast/Toast';
import { useHistory } from 'react-router-dom';

const TYPICAL_NAME_VARIANTS = {
  enGB: en['general.untitled_folder'],
  de: de['general.untitled_folder'],
  nl: nl['general.untitled_folder'],
};

/**
 * Mode: LIBRARY || ARCHIVE
 * */

export default function useFetchFoldersList(options) {
  const { t } = useTranslation();

  const iList = [];

  const { initList = iList, initQuery = '', archived = false } = options;

  // TODO: There's a possibility that list is undefined
  const [list, setList] = useState(initList);
  const [loading, setLoading] = useState(true);
  const [allFolders, setAllFolders] = useState([]);
  const [query, setQuery] = useState(initQuery);
  const [isEmptyList, setEmptyList] = useState(false);
  const [newFolderCreation, setNewFolderCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [usersOutsideCompany, setUsersOutsideCompany] = useState([]);
  const [isShowShareFolderModal, setIsShowShareFolderModal] = useState(false);
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const history = useHistory();

  const isSearching = useMemo(() => query !== initQuery, [initQuery, query]);
  const onSearch = useCallback(
    value => {
      setQuery(value);
    },
    [query]
  );

  const searchInput = useMemo(() => ({ value: query, onChange: onSearch }), [onSearch, query]);

  const setListSortedByDate = useCallback(
    newList => {
      const sortedByArchive = newList
        .filter(item => Boolean(item.archived) === archived)
        .map(item => ({ ...item, name: parseString(item.name) }));
      const sortedByDate = sortedByArchive
        .filter(item => item.name.toLowerCase().includes(query.toLowerCase()))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setList(sortedByDate.map(item => ({ ...item, createdAt: format(new Date(Date.parse(item.createdAt)), 'dd MMM yyyy') })));
    },
    [list, query]
  );

  const setAllFoldersSortedByDate = newList => {
    const sortedByDate = newList
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .map(item => ({ ...item, name: parseString(item.name) }));

    setAllFolders(sortedByDate.map(item => ({ ...item, createdAt: format(new Date(Date.parse(item.createdAt)), 'dd MMM yyyy') })));
  };

  // didMount & subsequent didUpdate with comparing previous state of query.searchterm against the current one.
  useEffect(() => {
    console.log(' // Folders list didMount & subsequent didUpdate', { query });
    let didCancel = false;
    const loadList = async () => {
      if (!didCancel) setLoading(true);

      try {
        const content = await CmsAPI.getFolders();
        setAllFoldersSortedByDate(content.result);
        if (!didCancel) {
          setListSortedByDate(content.result);
        }

        console.log('Folders list loadList', content.result.length, query);
        if (content.result.length === 0 && query === '') setEmptyList(true);
      } catch (e) {
        console.log(e.message);
      } finally {
        if (!didCancel) setLoading(false);
      }
    };
    loadList();
    return () => {
      didCancel = true;
    };
  }, [query]);

  const loadList = useCallback(async () => {
    console.log('load list trigerred query', query);
    if (isEmptyList) setEmptyList(false);
    setLoading(true);
    try {
      console.log('CmsAPI.getFolders query', query);
      const content = await CmsAPI.getFolders();
      console.log('query', query, 'content', content);
      setAllFoldersSortedByDate(content.result);
      setListSortedByDate(content.result);
      if (content.result.length === 0 && query === '') {
        setEmptyList(true);
      }
    } catch (e) {
      console.log(e.message);
    } finally {
      setLoading(false);
    }
  }, [isEmptyList, query]);

  const getNumbersFromFields = (array, value) => {
    return array
      .filter(item => item.name.includes(value))
      .map(item => {
        const result = Number.parseInt(item?.name.slice(value.length, item.length));
        return result ? result : 1;
      });
  };

  const updateFolderList = async () => {
    const { result } = await CmsAPI.getFolders();
    setAllFoldersSortedByDate(result);
  };

  const addNewFolderPlaceholder = useCallback(async () => {
    const untitledArray = [];

    for (const key in TYPICAL_NAME_VARIANTS) {
      console.log('allFolders', allFolders);
      const filteredArray = getNumbersFromFields(allFolders, TYPICAL_NAME_VARIANTS[key]);
      untitledArray.push(...filteredArray);
    }

    const sortedArray = untitledArray.sort((a, b) => a - b).filter(item => item < 1000);
    const defaultFolderName = `${t('general.untitled_folder')}${
      sortedArray.length > 0 ? ' ' + (sortedArray[sortedArray.length - 1] + 1) : ''
    }`;

    setNewFolderName(defaultFolderName);
    setNewFolderCreating(true);
  }, [list, newFolderCreation, allFolders]);

  const createFolder = useCallback(
    async name => {
      if (newFolderCreation && name) {
        const sameNameListItem = list.find(item => item.name === name);
        if (!sameNameListItem) {
          try {
            const response = await CmsAPI.insertFolder({ name });
            if (response.result) {
              const { result } = await CmsAPI.getFolders();
              setAllFoldersSortedByDate(result);
              setListSortedByDate(result);
            }

            setIsShowShareFolderModal(true);
          } catch (e) {
            if (e.message.includes('code 500')) throw new Error('Wrong request');
            else throw new Error(t('general.toast.error_folder_already_exist.part_one'));
          }
          setNewFolderCreating(false);
        } else {
          throw new Error(t('general.toast.error_folder_already_exist.part_one'));
        }
      }
    },
    [list, newFolderCreation]
  );

  const createFolderFromModal = async name => {
    const sameNameListItem = list.find(item => item.name === name);
    if (!sameNameListItem) {
      try {
        const response = await CmsAPI.insertFolder({ name });
        if (response.result) {
          const { result } = await CmsAPI.getFolders();
          setListSortedByDate(result);
          setAllFoldersSortedByDate(result);
        }
      } catch (e) {
        if (e.message.includes('code 500')) throw new Error('Wrong request');
        else throw new Error(t('general.toast.error_folder_already_exist.part_one'));
      }
      setNewFolderCreating(false);
    } else {
      throw new Error(t('general.toast.error_folder_already_exist.part_one'));
    }
  };

  const cancelFolderCreation = useCallback(() => {
    setNewFolderCreating(false);
  }, [list, newFolderCreation]);

  const sharedFolderWithUsers = async (folderId, usersInfo, isRedirect = false) => {
    const response = await CmsAPI.addUsersToFolder(folderId, usersInfo);
    if (response.result) {
      if (response.otherCompanyUsers.length > 0) {
        setUsersOutsideCompany(response.otherCompanyUsers);
      } else {
        setIsShowShareFolderModal(false);
        ToastNotification({
          message:
            usersInfo.users.length > 1
              ? `${t('general.toast.add_users_successfully_folders')}`
              : `${t('general.toast.add_user_successfully_folders')}`,
          iconName: 'shareFolder',
          isMobileView: isMobileView,
        });

        if (isRedirect) {
          history.push(`/eg/dashboard`, { team: true });
        }
      }
      const { result } = await CmsAPI.getFolders();
      setListSortedByDate(result);
      setAllFoldersSortedByDate(result);
    }
  };

  const editFolderUsers = async (folderId, users) => {
    Promise.all(users.map(({ username, folderRole }) => CmsAPI.editUserInSharedFolder(folderId, username, { folderRole }))).then(
      async () => {
        const { result } = await CmsAPI.getFolders();
        setListSortedByDate(result);
        setAllFoldersSortedByDate(result);
      }
    );
  };

  const deleteFolderUsers = async (folderId, users) => {
    Promise.all(users.map(({ username }) => CmsAPI.deleteUserInSharedFolder(folderId, username))).then(async () => {
      const { result } = await CmsAPI.getFolders();
      setListSortedByDate(result);
      setAllFoldersSortedByDate(result);
    });
  };

  const changeFolderEditingRight = async (folderId, editingRights) => {
    const response = await CmsAPI.updateFolder(folderId, { editingEnabled: editingRights });
    if (response.result) {
      const { result } = await CmsAPI.getFolders();
      setListSortedByDate(result);
      setAllFoldersSortedByDate(result);
    }
  };

  return {
    list,
    setList,
    allFolders,
    loadList,
    isEmptyList,
    setEmptyList,
    updateFolderList,
    isSearching,
    loading,
    searchInput,
    query,
    addNewFolderPlaceholder,
    createFolder,
    newFolderCreation,
    newFolderName,
    cancelFolderCreation,
    createFolderFromModal,
    sharedFolderWithUsers,
    editFolderUsers,
    deleteFolderUsers,
    changeFolderEditingRight,
    usersOutsideCompany,
    setUsersOutsideCompany,
    isShowShareFolderModal,
    setIsShowShareFolderModal,
  };
}
